import type { FocusType, PackageType, UsageStatus, WorkspaceStatus } from "../../types";
import type { AdminWorkspaceStatus } from "../adminWorkspaceStatus";

export interface AdminWorkspaceListItem {
  id: string;
  name: string;
  organizationId: string;
  organizationName?: string;
  packageType: PackageType;
  status: WorkspaceStatus;
  startDate: string;
  endDate: string;
  maxDepartments: number;
  departmentCount: number;
  submittedDepartmentCount: number;
  aiCallsUsed: number;
  techCostEstimateEur: number;
  usageStatus: UsageStatus;
}

export interface AdminDashboardCounts {
  organizations: number;
  workspaces: number;
  users: number;
  onboardingRequests: number;
  activeWorkspaces: number;
  archivedWorkspaces: number;
  expiredWorkspaces: number;
}

export interface AdminDashboardData {
  counts: AdminDashboardCounts;
  workspaces: AdminWorkspaceListItem[];
}

export interface AdminWorkspaceDetailData {
  workspace: {
    id: string;
    name: string;
    organizationId: string;
    packageType: PackageType;
    status: WorkspaceStatus;
    startDate: string;
    endDate: string;
    goal?: string;
    focusType: FocusType;
    maxDepartments: number;
  };
  organization: {
    id: string;
    name: string;
    industry?: string;
    companySize?: string;
  };
  owners: Array<{
    id: string;
    email: string;
    name?: string;
    isActive: boolean;
  }>;
  departments: Array<{
    id: string;
    name: string;
    participantUserId?: string;
    participantEmail?: string;
    status: string;
    inviteToken: string;
    responseStatus?: string;
    submittedAt?: string;
  }>;
  usage?: {
    aiInputTokensUsed: number;
    aiOutputTokensUsed: number;
    aiCallsUsed: number;
    aiCostEstimateEur: number;
    cloudflareCostEstimateEur: number;
    techCostEstimateEur: number;
    techBudgetLimitEur: number;
    usageStatus: UsageStatus;
  };
  latestSummary?: {
    id: string;
    summaryType: string;
    model: string;
    createdAt: string;
  };
  auditLogs: Array<{
    id: string;
    action: string;
    detailsJson: string;
    createdAt: string;
  }>;
}

export const emptyAdminDashboard: AdminDashboardData = {
  counts: {
    organizations: 0,
    workspaces: 0,
    users: 0,
    onboardingRequests: 0,
    activeWorkspaces: 0,
    archivedWorkspaces: 0,
    expiredWorkspaces: 0
  },
  workspaces: []
};

async function parseJson<T>(response: Response): Promise<T> {
  return (await response.json()) as T;
}

async function assertOk(response: Response): Promise<void> {
  if (response.ok) return;
  const data = await response.json().catch(() => undefined) as { error?: string } | undefined;
  throw new Error(data?.error ?? "Admin-Daten konnten nicht geladen werden.");
}

export function normalizeAdminDashboardResponse(input: Partial<AdminDashboardData> | undefined): AdminDashboardData {
  return {
    counts: {
      ...emptyAdminDashboard.counts,
      ...input?.counts
    },
    workspaces: input?.workspaces ?? []
  };
}

export async function fetchAdminDashboard(): Promise<AdminDashboardData> {
  const response = await fetch("/api/admin/dashboard", { credentials: "include" });
  await assertOk(response);
  return normalizeAdminDashboardResponse(await parseJson<AdminDashboardData>(response));
}

export async function fetchAdminWorkspaceDetail(workspaceId: string): Promise<AdminWorkspaceDetailData> {
  const response = await fetch(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}`, { credentials: "include" });
  await assertOk(response);
  return parseJson<AdminWorkspaceDetailData>(response);
}

export async function updateAdminWorkspaceStatus(
  workspaceId: string,
  status: AdminWorkspaceStatus
): Promise<AdminWorkspaceDetailData["workspace"]> {
  const response = await fetch(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/status`, {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json"
    },
    body: JSON.stringify({ status })
  });
  await assertOk(response);
  const data = await parseJson<{ workspace: AdminWorkspaceDetailData["workspace"] }>(response);
  return data.workspace;
}
